import { supabase } from '@/integrations/supabase/client';
import { devLog } from '@/utils/logger';
import type { TimeEntry, ClockInRequest, ClockOutRequest, CurrentTimeEntryResponse } from './types';
import { GeolocationError } from './types';

export const timeAttendanceService = {
  // Validate user session before making authenticated requests
  async validateSession(): Promise<{ valid: boolean; error?: string }> {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Session validation error:', error);
        return { valid: false, error: 'Failed to validate session' };
      }

      if (!session) {
        console.error('No active session found');
        return { valid: false, error: 'No active session. Please log in again.' };
      }

      // Check if session is expired
      const expiresAt = session.expires_at;
      if (expiresAt && expiresAt * 1000 < Date.now()) {
        console.error('Session expired');
        return { valid: false, error: 'Session expired. Please log in again.' };
      }

      return { valid: true };
    } catch (error) {
      console.error('Unexpected error validating session:', error);
      return { valid: false, error: 'Authentication error occurred' };
    }
  },

  // Get user's IP address for auditing
  async getUserIP(): Promise<string | null> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Failed to get IP address:', error);
      return null;
    }
  },

  // Get current time entry status for a user
  async getCurrentTimeEntry(profileId: string): Promise<CurrentTimeEntryResponse> {
    const { data, error } = await supabase
      .from('time_entries')
      .select('id, clock_in, clock_out, ip_address_in, ip_address_out, profile_id, company_id, geo_status')
      .eq('profile_id', profileId)
      .order('clock_in', { ascending: false })
      .limit(1);

    if (error) {
      throw new Error(`Failed to fetch current time entry: ${error.message}`);
    }

    // If there's an entry and clock_out is null, user is clocked in
    const latestEntry = data && data.length > 0 ? data[0] : null;
    const isLoggedIn = latestEntry !== null && latestEntry.clock_out === null;

    // If there's a current entry, fetch unresolved attendance flags
    let attendanceFlags: Array<any> = [];
    if (latestEntry) {
      const { data: flagsData, error: flagsError } = await supabase
        .from('attendance_event_flags')
        .select('id, flag_type, flag_severity, flag_reason, event_date, event_time')
        .eq('time_entry_id', latestEntry.id)
        .is('resolved_at', null)
        .order('created_at', { ascending: false });

      if (!flagsError && flagsData) {
        attendanceFlags = flagsData;
      }
    }

    return {
      entry: latestEntry ? { ...latestEntry, attendance_flags: attendanceFlags } : null,
      isLoggedIn
    };
  },

  // Clock in a user
  async clockIn(request: ClockInRequest): Promise<TimeEntry> {
    // Validate session first
    const sessionCheck = await this.validateSession();
    if (!sessionCheck.valid) {
      throw new Error(sessionCheck.error || 'Authentication failed');
    }

    devLog('Clock in - Session validated, calling edge function');

    const { data, error } = await supabase.functions.invoke('record-time-entry-geo', {
      body: {
        event_type: 'CLOCK_IN',
        profile_id: request.profile_id,
        company_id: request.company_id,
        ip_address: request.ip_address_in,
        geo_location: request.geo_location,
      },
    });

    // Check if data contains an error (200 response with error details)
    if (data?.code === 'UNSCHEDULED_CHECKIN_BLOCKED') {
      const userMessage = data.message || 'Unscheduled check-ins are not allowed';
      throw new Error(userMessage);
    }

    // Check for other error responses in data
    if (data?.error && !data?.time_entry) {
      const errorDetails = data;
      
      // Check for unscheduled check-in errors
      if (errorDetails.code === 'UNSCHEDULED_CHECKIN_BLOCKED') {
        const userMessage = errorDetails.message || 'Unscheduled check-ins are not allowed';
        throw new Error(userMessage);
      }

      // Integrity violations (already clocked in / invalid time range / tenant mismatch)
      if (
        errorDetails.code === 'ALREADY_CLOCKED_IN' ||
        errorDetails.code === 'TIME_ENTRY_INTEGRITY_VIOLATION'
      ) {
        throw new Error(errorDetails.message || errorDetails.error || 'Clock in rejected');
      }

      // Handle geolocation errors
      const { error: errorMsg, geoStatus, distance, allowedRadius, locationName, locationAddress, geofence, flagReason } = errorDetails;
      
      let userMessage = errorMsg || 'Failed to clock in';

      if (geoStatus === 'NO_LOCATION') {
        userMessage = 'Location access is required when strict mode is enabled. Please enable location services and try again.';
      } else if (geoStatus === 'NO_GEOFENCE_CONFIGURED') {
        userMessage = 'No approved office locations have been configured. Please contact your administrator.';
      } else if (geoStatus === 'OUTSIDE_RADIUS' && distance && allowedRadius) {
        const locName = locationName || geofence?.label || 'the approved location';
        userMessage = `You are outside ${locName}. You are ${Math.round(distance)}m away, but must be within ${allowedRadius}m to clock in.`;
      } else if (flagReason) {
        userMessage = `Location validation failed: ${flagReason}`;
      }

      throw new GeolocationError(
        userMessage,
        geoStatus,
        distance,
        allowedRadius || geofence?.radius_meters,
        locationName || geofence?.label,
        locationAddress || geofence?.address,
        flagReason
      );
    }

    // Check for errors from edge function invocation
    if (error) {
      console.error('Clock in edge function error:', error);
      
      // Try to extract detailed error information from error.context
      let errorDetails = null;
      
      if (error.context) {
        try {
          errorDetails = await error.context.json();
          devLog('Extracted error details from context:', errorDetails);
        } catch (e) {
          devLog('Could not parse error context:', e);
        }
      }

      // If we have detailed error information, create user-friendly messages
      if (errorDetails) {
        // FIRST: Check for unscheduled check-in errors (non-geolocation)
        if (errorDetails.code === 'UNSCHEDULED_CHECKIN_BLOCKED') {
          const userMessage = errorDetails.message || 'Unscheduled check-ins are not allowed';
          throw new Error(userMessage);
        }

        // Integrity violations surfaced as HTTP 409 by the edge function
        if (
          errorDetails.code === 'ALREADY_CLOCKED_IN' ||
          errorDetails.code === 'TIME_ENTRY_INTEGRITY_VIOLATION'
        ) {
          throw new Error(errorDetails.message || errorDetails.error || 'Clock in rejected');
        }

        // THEN: Handle geolocation errors
        const { error: errorMsg, geoStatus, distance, allowedRadius, locationName, locationAddress, geofence, flagReason } = errorDetails;
        
        // Create user-friendly error messages based on specific error types
        let userMessage = errorMsg || 'Failed to clock in';

        if (geoStatus === 'NO_LOCATION') {
          userMessage = 'Location access is required when strict mode is enabled. Please enable location services and try again.';
        } else if (geoStatus === 'NO_GEOFENCE_CONFIGURED') {
          userMessage = 'No approved office locations have been configured. Please contact your administrator.';
        } else if (geoStatus === 'OUTSIDE_RADIUS' && distance && allowedRadius) {
          const locName = locationName || geofence?.label || 'the approved location';
          userMessage = `You are outside ${locName}. You are ${Math.round(distance)}m away, but must be within ${allowedRadius}m to clock in.`;
        } else if (flagReason) {
          userMessage = `Location validation failed: ${flagReason}`;
        }

        throw new GeolocationError(
          userMessage,
          geoStatus,
          distance,
          allowedRadius || geofence?.radius_meters,
          locationName || geofence?.label,
          locationAddress || geofence?.address,
          flagReason
        );
      }
      
      // Handle authentication errors
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        throw new Error('Authentication failed. Please log out and log in again.');
      }
      
      throw new Error(error.message || 'Failed to clock in');
    }

    if (!data?.time_entry) {
      throw new Error('Invalid response from server');
    }

    devLog('Clock in successful');
    return data.time_entry;
  },

  // Clock out a user
  async clockOut(request: ClockOutRequest): Promise<TimeEntry> {
    // Validate session first
    const sessionCheck = await this.validateSession();
    if (!sessionCheck.valid) {
      throw new Error(sessionCheck.error || 'Authentication failed');
    }

    devLog('Clock out - Session validated, calling edge function');

    const { data, error } = await supabase.functions.invoke('record-time-entry-geo', {
      body: {
        event_type: 'CLOCK_OUT',
        entry_id: request.entry_id,
        ip_address: request.ip_address_out,
        geo_location: request.geo_location,
      },
    });

    // Check if data contains an error (200 response with error details)
    if (data?.code === 'UNSCHEDULED_CHECKIN_BLOCKED') {
      const userMessage = data.message || 'Unscheduled check-ins are not allowed';
      throw new Error(userMessage);
    }

    // Check for other error responses in data
    if (data?.error && !data?.time_entry) {
      const errorDetails = data;
      
      // Check for unscheduled check-in errors
      if (errorDetails.code === 'UNSCHEDULED_CHECKIN_BLOCKED') {
        const userMessage = errorDetails.message || 'Unscheduled check-ins are not allowed';
        throw new Error(userMessage);
      }

      // Handle geolocation errors
      const { error: errorMsg, geoStatus, distance, allowedRadius, locationName, locationAddress, geofence, flagReason } = errorDetails;
      
      let userMessage = errorMsg || 'Failed to clock out';

      if (geoStatus === 'NO_LOCATION') {
        userMessage = 'Location access is required when strict mode is enabled. Please enable location services and try again.';
      } else if (geoStatus === 'NO_GEOFENCE_CONFIGURED') {
        userMessage = 'No approved office locations have been configured. Please contact your administrator.';
      } else if (geoStatus === 'OUTSIDE_RADIUS' && distance && allowedRadius) {
        const locName = locationName || geofence?.label || 'the approved location';
        userMessage = `You are outside ${locName}. You are ${Math.round(distance)}m away, but must be within ${allowedRadius}m to clock out.`;
      } else if (flagReason) {
        userMessage = `Location validation failed: ${flagReason}`;
      }

      throw new GeolocationError(
        userMessage,
        geoStatus,
        distance,
        allowedRadius || geofence?.radius_meters,
        locationName || geofence?.label,
        locationAddress || geofence?.address,
        flagReason
      );
    }

    // Check for errors from edge function invocation
    if (error) {
      console.error('Clock out edge function error:', error);
      
      // Try to extract detailed error information from error.context
      let errorDetails = null;
      
      if (error.context) {
        try {
          errorDetails = await error.context.json();
          devLog('Extracted error details from context:', errorDetails);
        } catch (e) {
          devLog('Could not parse error context:', e);
        }
      }

      // If we have detailed error information, create user-friendly messages
      if (errorDetails) {
        // FIRST: Check for unscheduled check-in errors (non-geolocation)
        if (errorDetails.code === 'UNSCHEDULED_CHECKIN_BLOCKED') {
          const userMessage = errorDetails.message || 'Unscheduled check-ins are not allowed';
          throw new Error(userMessage);
        }

        // THEN: Handle geolocation errors
        const { error: errorMsg, geoStatus, distance, allowedRadius, locationName, locationAddress, geofence, flagReason } = errorDetails;
        
        // Create user-friendly error messages based on specific error types
        let userMessage = errorMsg || 'Failed to clock out';

        if (geoStatus === 'NO_LOCATION') {
          userMessage = 'Location access is required when strict mode is enabled. Please enable location services and try again.';
        } else if (geoStatus === 'NO_GEOFENCE_CONFIGURED') {
          userMessage = 'No approved office locations have been configured. Please contact your administrator.';
        } else if (geoStatus === 'OUTSIDE_RADIUS' && distance && allowedRadius) {
          const locName = locationName || geofence?.label || 'the approved location';
          userMessage = `You are outside ${locName}. You are ${Math.round(distance)}m away, but must be within ${allowedRadius}m to clock out.`;
        } else if (flagReason) {
          userMessage = `Location validation failed: ${flagReason}`;
        }

        throw new GeolocationError(
          userMessage,
          geoStatus,
          distance,
          allowedRadius || geofence?.radius_meters,
          locationName || geofence?.label,
          locationAddress || geofence?.address,
          flagReason
        );
      }
      
      // Handle authentication errors
      if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
        throw new Error('Authentication failed. Please log out and log in again.');
      }
      
      throw new Error(error.message || 'Failed to clock out');
    }

    if (!data?.time_entry) {
      throw new Error('Invalid response from server');
    }

    devLog('Clock out successful');
    return data.time_entry;
  }
};
